import { useEffect, useState } from 'react';
import axios from 'axios';
import { Eye, EyeOff, GripVertical, Settings, Save, ChevronDown, ChevronUp, Package } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTION_ICONS = {
  featured: '⭐',
  best_seller: '🔥',
  new_arrival: '✨',
  trending: '📈',
  super_selling: '🚀',
  premium_collection: '👑',
  offers: '🏷️'
};

export const AdminHomepageSections = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    loadSections();
    loadProducts();
  }, []);

  const loadSections = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/homepage-sections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSections(response.data.sections || []);
    } catch (error) {
      toast.error('Failed to load sections');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    setLoadingProducts(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  };

  const toggleVisibility = async (section) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/homepage-sections/${section.id}`, {
        is_visible: !section.is_visible
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadSections();
      toast.success(`Section ${!section.is_visible ? 'visible' : 'hidden'}`);
    } catch (error) {
      toast.error('Failed to update section');
    }
  };

  const moveSection = async (index, direction) => {
    const newSections = [...sections];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newSections.length) return;
    
    // Swap positions
    [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];
    
    // Update display orders
    const updates = newSections.map((s, i) => ({ id: s.id, display_order: i }));
    
    setSections(newSections);
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/homepage-sections/reorder`, {
        sections: updates
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order updated');
    } catch (error) {
      loadSections(); // Revert on error
      toast.error('Failed to reorder');
    }
  };

  const saveSection = async (sectionId, data) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/homepage-sections/${sectionId}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Section updated');
      setEditingSection(null);
      loadSections();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-homepage-sections">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-light text-charcoal">Homepage Sections</h1>
        <p className="text-charcoal-light text-sm mt-1">
          Configure which product sections appear on your homepage and their order
        </p>
      </div>

      {/* Sections List */}
      <div className="bg-white border border-charcoal/10">
        <div className="p-4 border-b border-charcoal/10 bg-beige/30">
          <div className="grid grid-cols-12 gap-4 text-xs tracking-wider uppercase text-charcoal-light font-medium">
            <div className="col-span-1">Order</div>
            <div className="col-span-4">Section</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Items</div>
            <div className="col-span-3">Actions</div>
          </div>
        </div>
        
        <div className="divide-y divide-charcoal/5">
          {sections.map((section, index) => (
            <div key={section.id}>
              <div className={`p-4 ${!section.is_visible ? 'bg-gray-50' : ''}`}>
                <div className="grid grid-cols-12 gap-4 items-center">
                  {/* Order Controls */}
                  <div className="col-span-1 flex flex-col gap-1">
                    <button
                      onClick={() => moveSection(index, 'up')}
                      disabled={index === 0}
                      className="p-1 hover:bg-beige disabled:opacity-30 transition-colors"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => moveSection(index, 'down')}
                      disabled={index === sections.length - 1}
                      className="p-1 hover:bg-beige disabled:opacity-30 transition-colors"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Section Info */}
                  <div className="col-span-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{SECTION_ICONS[section.section_type] || '📦'}</span>
                      <div>
                        <h3 className="font-medium text-charcoal">{section.title}</h3>
                        <p className="text-xs text-charcoal-light">{section.subtitle}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Status */}
                  <div className="col-span-2">
                    <button
                      onClick={() => toggleVisibility(section)}
                      className={`inline-flex items-center gap-2 px-3 py-1 text-sm ${
                        section.is_visible 
                          ? 'bg-green-50 text-green-700' 
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {section.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      {section.is_visible ? 'Visible' : 'Hidden'}
                    </button>
                  </div>
                  
                  {/* Items */}
                  <div className="col-span-2">
                    <span className="text-sm text-charcoal">
                      {section.auto_populate ? (
                        <span className="text-charcoal-light">Auto (max {section.max_items})</span>
                      ) : (
                        <span>{section.product_ids?.length || 0} selected</span>
                      )}
                    </span>
                  </div>
                  
                  {/* Actions */}
                  <div className="col-span-3 flex items-center gap-2">
                    <button
                      onClick={() => setEditingSection(editingSection === section.id ? null : section.id)}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-charcoal-light hover:text-gold hover:bg-beige transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      Configure
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Edit Panel */}
              {editingSection === section.id && (
                <div className="p-6 bg-beige/30 border-t border-charcoal/10">
                  <SectionEditForm
                    section={section}
                    products={products}
                    onSave={(data) => saveSection(section.id, data)}
                    onCancel={() => setEditingSection(null)}
                    saving={saving}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 p-4">
        <h4 className="font-medium text-blue-800 mb-2">How it works</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>Auto-populate:</strong> Products are automatically pulled based on their toggle flags (e.g., Featured products appear in Featured section)</li>
          <li>• <strong>Manual selection:</strong> Disable auto-populate to hand-pick specific products for each section</li>
          <li>• <strong>Reorder:</strong> Use the arrows to change the display order on your homepage</li>
          <li>• <strong>Hide sections:</strong> Toggle visibility to temporarily hide a section without losing its configuration</li>
        </ul>
      </div>
    </div>
  );
};

// Section Edit Form Component
const SectionEditForm = ({ section, products, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    title: section.title || '',
    subtitle: section.subtitle || '',
    auto_populate: section.auto_populate !== false,
    max_items: section.max_items || 8,
    product_ids: section.product_ids || []
  });

  const handleSubmit = () => {
    onSave(formData);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
            Section Title
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full h-10 px-3 border border-charcoal/10 focus:border-gold outline-none"
          />
        </div>
        <div>
          <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
            Subtitle
          </label>
          <input
            type="text"
            value={formData.subtitle}
            onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
            className="w-full h-10 px-3 border border-charcoal/10 focus:border-gold outline-none"
          />
        </div>
      </div>
      
      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.auto_populate}
            onChange={(e) => setFormData({ ...formData, auto_populate: e.target.checked })}
            className="w-4 h-4 accent-gold"
          />
          <span className="text-sm text-charcoal">Auto-populate from product toggles</span>
        </label>
        
        <div className="flex items-center gap-2">
          <label className="text-sm text-charcoal-light">Max items:</label>
          <input
            type="number"
            value={formData.max_items}
            onChange={(e) => setFormData({ ...formData, max_items: parseInt(e.target.value) || 8 })}
            className="w-16 h-8 px-2 border border-charcoal/10 focus:border-gold outline-none text-center"
            min="1"
            max="20"
          />
        </div>
      </div>
      
      {/* Manual Product Selection */}
      {!formData.auto_populate && (
        <div>
          <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
            Select Products
          </label>
          <div className="max-h-48 overflow-y-auto border border-charcoal/10 p-2 grid grid-cols-2 md:grid-cols-3 gap-2">
            {products.map(product => (
              <label
                key={product.id}
                className={`flex items-center gap-2 p-2 cursor-pointer ${
                  formData.product_ids.includes(product.id) ? 'bg-gold/10 border border-gold' : 'bg-white border border-transparent hover:bg-beige'
                }`}
              >
                <input
                  type="checkbox"
                  checked={formData.product_ids.includes(product.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFormData({ ...formData, product_ids: [...formData.product_ids, product.id] });
                    } else {
                      setFormData({ ...formData, product_ids: formData.product_ids.filter(id => id !== product.id) });
                    }
                  }}
                  className="w-4 h-4 accent-gold"
                />
                <img src={product.images?.[0] || ''} alt="" className="w-8 h-8 object-cover" />
                <span className="text-xs text-charcoal truncate">{product.name}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-charcoal-light mt-1">Selected: {formData.product_ids.length} products</p>
        </div>
      )}
      
      <div className="flex justify-end gap-3 pt-4">
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-charcoal/20 text-charcoal text-sm"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-gold text-white text-sm disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default AdminHomepageSections;
